/* La memoria de Makai por usuario. Vive en la API (`/api/memoria`); aquí se
   manipula en local y se guarda entera con PUT en cada cambio. */
import { api, type Memoria, type Perfil, type Nota } from "./api";
import { estado } from "./estado";
import { uid, ahora } from "./ui";

const ETIQUETA: Record<string, string> = {
  apodo: "cómo llamarla", rol: "su rol", trabajo: "qué hace", foco: "sus cuentas de foco", estilo: "el estilo de respuesta",
  formato: "el formato", horasCuando: "cuándo imputa horas", quitaTiempo: "qué le quita tiempo", evitar: "qué evitar", idioma: "el idioma",
};

async function persistir(m: Memoria): Promise<Memoria> {
  const guardada = await api.guardarMemoria({ perfil: m.perfil, notas: m.notas, cambios: m.cambios });
  estado.memoria = guardada;
  return guardada;
}

function borrador(): Memoria {
  const u = estado.usuario()!;
  return { usuarioId: u.id, version: 0, perfil: {}, notas: [], cambios: [], creadaEn: ahora(), actualizadaEn: ahora() };
}

export const MemoriaLocal = {
  async cargar() { estado.memoria = await api.memoria().catch(() => null); return estado.memoria; },

  async crear(perfil: Perfil) {
    const m = borrador();
    m.perfil = { apodo: "", rol: "", trabajo: "", foco: [], estilo: "Con contexto y fuentes", formato: "Viñetas", horasCuando: "Al final del día", quitaTiempo: "", evitar: "", idioma: "ES", ...perfil };
    m.cambios.unshift({ id: uid(), fecha: ahora(), tipo: "onboarding", texto: "Makai creó la memoria a partir de la conversación de bienvenida." });
    return persistir(m);
  },

  async actualizarPerfil(patch: Perfil, origen = "perfil") {
    const m = estado.memoria; if (!m) return null;
    const cambios: string[] = [];
    Object.keys(patch).forEach((k) => { if (JSON.stringify(m.perfil[k]) !== JSON.stringify(patch[k])) { m.perfil[k] = patch[k]; cambios.push(k); } });
    if (!cambios.length) return m;
    m.cambios.unshift({ id: uid(), fecha: ahora(), tipo: origen, texto: "Actualizó " + cambios.map((k) => ETIQUETA[k] || k).join(", ") + "." });
    return persistir(m);
  },

  async nota(texto: string, origen = "makai") {
    const m = estado.memoria; if (!m) return null;
    const n: Nota = { id: uid(), texto: texto.trim(), origen, fecha: ahora() };
    m.notas.unshift(n);
    m.cambios.unshift({ id: uid(), fecha: ahora(), tipo: "nota", texto: "Guardó: “" + n.texto + "”" });
    await persistir(m); return n;
  },

  async olvidar(notaId: string) {
    const m = estado.memoria; if (!m) return;
    const n = m.notas.find((x) => x.id === notaId);
    m.notas = m.notas.filter((x) => x.id !== notaId);
    if (n) m.cambios.unshift({ id: uid(), fecha: ahora(), tipo: "olvido", texto: "Olvidó: “" + n.texto + "”" });
    await persistir(m);
  },

  // Lo que Makai observa del usuario al usar la plataforma.
  async evento(tipo: string, texto: string) {
    const m = estado.memoria; if (!m) return;
    m.cambios.unshift({ id: uid(), fecha: ahora(), tipo, texto });
    m.cambios = m.cambios.slice(0, 60);
    try { await persistir(m); } catch { /* observación perdida: no bloquea nada */ }
  },

  async borrar() {
    const m = borrador();
    await persistir(m);
    estado.memoria = null;
  },

  ultimos(n = 6) { return (estado.memoria?.cambios || []).slice(0, n); },

  lectura() {
    const m = estado.memoria; if (!m) return null;
    const p = m.perfil; const hechos: { k: string; t: string; id?: string }[] = [];
    if (p.apodo) hechos.push({ k: "apodo", t: "Prefiere que la llamen " + p.apodo });
    if (p.estilo) hechos.push({ k: "estilo", t: "Respuestas: " + String(p.estilo).toLowerCase() });
    if (p.formato) hechos.push({ k: "formato", t: "Formato: " + String(p.formato).toLowerCase() });
    if (p.foco && p.foco.length) hechos.push({ k: "foco", t: "Cuentas de foco: " + p.foco.map((id) => estado.nombreCliente(id)).join(", ") });
    if (p.horasCuando) hechos.push({ k: "horas", t: "Imputa horas: " + String(p.horasCuando).toLowerCase() });
    if (p.quitaTiempo) hechos.push({ k: "quitaTiempo", t: "Le quita tiempo: " + p.quitaTiempo });
    if (p.evitar) hechos.push({ k: "evitar", t: "Evitar: " + p.evitar });
    m.notas.forEach((n) => hechos.push({ k: "nota", t: n.texto, id: n.id }));
    return { perfil: p, hechos, ultimos: m.cambios.slice(0, 6), version: m.version, actualizada: m.actualizadaEn };
  },
};

// Memoria "vacía" que la API pudo devolver tras un borrado: sin perfil, sin cambios.
export function memoriaEsReal(m: Memoria | null): m is Memoria {
  return !!m && Object.keys(m.perfil || {}).length > 0;
}
